import { prefersReducedMotion } from '@theme/utilities';

/**
 * Typewriter rotation: type phrase → pause → delete → next phrase (loop).
 */
class TextTypewriter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const signal = this.#abort.signal;

    const raw = this.dataset.phrases;
    if (!raw) {
      return;
    }

    let phrases;
    try {
      phrases = JSON.parse(raw);
    } catch {
      return;
    }

    if (!Array.isArray(phrases)) {
      return;
    }

    const list = phrases.map((p) => String(p ?? '').trim()).filter(Boolean);
    if (list.length === 0) {
      return;
    }

    const out = this.querySelector('[data-typewriter-out]');
    const cursor = this.querySelector('[data-typewriter-cursor]');
    if (!(out instanceof HTMLElement)) {
      return;
    }

    if (prefersReducedMotion()) {
      out.textContent = list[0] ?? '';
      if (cursor instanceof HTMLElement) {
        cursor.hidden = true;
      }
      return;
    }

    const typingMs = Math.max(8, Number(this.dataset.typingMs) || 45);
    const deletingMs = Math.max(4, Number(this.dataset.deletingMs) || 30);
    const pauseTyped = Math.max(0, Number(this.dataset.pauseTypedMs) || 1600);
    const pauseBetween = Math.max(0, Number(this.dataset.pauseBetweenMs) || 350);

    this.#runLoop(out, list, typingMs, deletingMs, pauseTyped, pauseBetween, signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {HTMLElement} out
   * @param {string[]} list
   * @param {number} typingMs
   * @param {number} deletingMs
   * @param {number} pauseTyped
   * @param {number} pauseBetween
   * @param {AbortSignal} signal
   */
  async #runLoop(out, list, typingMs, deletingMs, pauseTyped, pauseBetween, signal) {
    let index = 0;

    /** @param {number} ms */
    const sleep = (ms) =>
      new Promise((resolve) => {
        const id = setTimeout(() => resolve(undefined), ms);
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(id);
            resolve(undefined);
          },
          { once: true }
        );
      });

    while (!signal.aborted) {
      const phrase = list[index % list.length] ?? '';
      index += 1;

      for (let i = 0; i <= phrase.length; i++) {
        if (signal.aborted) {
          return;
        }
        out.textContent = phrase.slice(0, i);
        await sleep(typingMs);
      }

      await sleep(pauseTyped);
      if (signal.aborted) {
        return;
      }

      for (let i = phrase.length; i >= 0; i--) {
        if (signal.aborted) {
          return;
        }
        out.textContent = phrase.slice(0, i);
        await sleep(deletingMs);
      }

      await sleep(pauseBetween);
    }
  }
}

if (!customElements.get('text-typewriter-ui')) {
  customElements.define('text-typewriter-ui', TextTypewriter);
}
